import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import _ from 'lodash';
import { useState, useEffect } from 'react';
import {
  fetchGoogleCalendarEvents,
  fetchOutlookCalendarEvents,
  getCalendarAccounts,
  getOutlookCalendarAccounts,
  ICalendarAccount,
  IEvent,
  IOutlookCalendarAccount,
  IOutlookCalendarEvent,
} from 'wisestep-api';
import {
  getDateUsingFormat,
  WsColors,
  WsDrawer,
  WsElementWithPopOver,
  WsIconMoreVer,
  WsIconText,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import StageStatusCalendarEvent from '../../../../../components/CalendarEvent/StageStatusCalendarEvent';
import { BlankTemplateStyledBox } from '../../../../../components/TemplateStyled/CommonTemplateStled';
import { useFetch } from '../../../../../hooks/useFetch';
import { useFetchDeps } from '../../../../../hooks/useFetchDeps';
import NoEventImage from '../../../../../images/NoEvent.svg';
import useCopyToClipboard from '../../../../candidates/talentpool/hooks/useCopyToClipboard';
import { CalendarEventCardEditDialog } from '../../../../home/components/CalendarBoard';

import TeamAvatarGroupEvent from './TeamAvatarGroupEvent';

const StyledGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
});

const StyledOpenIcon = styled(OpenInNewIcon)({
  height: '20px',
  color: WsColors.TheBlues400,
});

const StyledTypography = styled(Typography)({
  color: WsColors.TheBlues400,
  fontWeight: 'bold',
});

const StyledKeyboardArrowDownIcon = styled(KeyboardArrowDownIcon)({
  color: WsColors.TheBlues400,
});

const StyledKeyboardArrowUpIcon = styled(KeyboardArrowUpIcon)({
  color: WsColors.TheBlues400,
});

export const CandidateEvent = ({ setTabIndex, setEvalTemplateId, candidate, setFlag }) => {
  const dayStart = new Date('2023-11-22T18:30:00.777Z');
  dayStart.setHours(0, 0, 0);
  const dayEnd = new Date();
  dayEnd.setHours(23, 59, 59);
  dayEnd.setFullYear(dayEnd.getFullYear() + 1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarObj, setCalendarObj] = useState<IEvent>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [, copy] = useCopyToClipboard();
  const [expanded, setExpanded] = useState({});
  const [eventsFlag, setEventsFlag] = useState(false);
  const [calendarEventAccountsGoogle] = useFetch<ICalendarAccount[]>(getCalendarAccounts);
  const [calendarAccountsOutlook] = useFetch<IOutlookCalendarAccount[]>(getOutlookCalendarAccounts);
  const [googleEvents, setGoogleEvents] = useState<IEvent[]>([]);
  const [outlookEvents, setOutlookEvents] = useState<IEvent[]>([]);

  useEffect(() => {
    const fetchGoogleEvents = async () => {
      if (!calendarEventAccountsGoogle || calendarEventAccountsGoogle.length === 0) {
        setGoogleEvents([]);
        return;
      }

      try {
        const allGoogleEvents = await Promise.all(
          calendarEventAccountsGoogle.map((account) =>
            fetchGoogleCalendarEvents({
              googleOAuth: account,
              from: dayStart,
              attendees: [],
              entityId: null,
              entityType: '',
              eventDescription: '',
              eventSummary: '',
              location: '',
              reminders: [],
              to: dayEnd,
              jobCandidateInteractionId: candidate.id,
            })
          )
        );

        const flattenedEvents = allGoogleEvents.flatMap((response) => response.data);
        setGoogleEvents(flattenedEvents);
      } catch (error) {
        console.error('Error fetching Google events:', error);
        setGoogleEvents([]);
      }
    };

    fetchGoogleEvents();
  }, [calendarEventAccountsGoogle, eventsFlag]);

  useEffect(() => {
    const fetchOutlookEvents = async () => {
      if (!calendarAccountsOutlook || calendarAccountsOutlook.length === 0) {
        setOutlookEvents([]);
        return;
      }

      try {
        const allOutlookEvents = await Promise.all(
          calendarAccountsOutlook.map((account) =>
            fetchOutlookCalendarEvents({
              outlookOAuthToken: account,
              from: dayStart,
              attendees: [],
              entityId: null,
              entityType: '',
              eventDescription: '',
              eventSummary: '',
              location: '',
              reminders: [],
              to: dayEnd,
              jobCandidateInteractionId: candidate.id,
            })
          )
        );

        const flattenedEvents = allOutlookEvents.flatMap((response) => response.data);
        setOutlookEvents(flattenedEvents);
      } catch (error) {
        console.error('Error fetching Outlook events:', error);
        setOutlookEvents([]);
      }
    };

    fetchOutlookEvents();
  }, [calendarAccountsOutlook, eventsFlag]);

  const handleUpcomingToggle = (eventId) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [eventId]: !prevExpanded[eventId],
    }));
  };

  const editButtonHandler = (eventData: IEvent) => {
    setCalendarObj(eventData);
    setDialogOpen(true);
  };

  const viewEvaluationHandler = (evalTemplateId: string) => {
    setEvalTemplateId(evalTemplateId);
    setTabIndex(4);
    setFlag((flag) => !flag);
  };

  return (
    <>
      <Grid container padding={2} xs={12}>
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <Button onClick={() => setDrawerOpen(true)} variant="contained">
            Add Event
          </Button>
        </Grid>

        {googleEvents && outlookEvents && [...googleEvents, ...outlookEvents].length > 0 ? (
          _(
            [
              ...(googleEvents?.map((e) => ({ ...e, type: 'GOOGLE' })) ?? []),
              ...(outlookEvents?.map((e) => ({ ...e, type: 'OUTLOOK' })) ?? []),
            ]
              .filter((e) => e.jobCandidateInteractionId === candidate.id)
              .sort((a, b) => +b.from - +a.from)
          )
            .groupBy((value) => new Date(value.from).toDateString())
            .map((events, date) => (
              <Grid key={date} container sx={{ mt: 3 }} xs={12}>
                <Grid item xs={12}>
                  <WsTypography opacity>{date}</WsTypography>
                </Grid>
                <Grid item xs={12}>
                  {events.map((e: IEvent, i) => (
                    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mt: 2 }} key={i}>
                      <Grid
                        container
                        alignItems="center"
                        display="flex"
                        justifyContent="space-between"
                        xs={12}
                      >
                        <Grid item>
                          <WsTypography variant={WsTypoVariants.TITLE} bold>
                            {e.eventSummary}
                          </WsTypography>
                        </Grid>
                        {e.meeting && (
                          <Grid
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            spacing={2}
                          >
                            <Grid item>
                              <Tooltip title="Join Meeting">
                                <Button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    window.open(e?.meeting ?? '', '_blank');
                                  }}
                                  variant="outlined"
                                >
                                  Join meeting
                                </Button>
                              </Tooltip>
                            </Grid>
                            <Grid item>
                              <WsElementWithPopOver element={<WsIconMoreVer />}>
                                <Box p={2}>
                                  <Tooltip title="Copy Link">
                                    <WsIconText
                                      text="Copy Link"
                                      icon={<ContentCopyIcon />}
                                      textSize={WsTypoVariants.BODY1}
                                      textCursor="pointer"
                                      textColor={WsTypoColors.BLACK}
                                      onClick={() => copy(e?.meeting ?? '')}
                                    />
                                  </Tooltip>
                                  <Box mt={1}>
                                    <WsIconText
                                      text="Edit"
                                      icon={<EditIcon />}
                                      textSize={WsTypoVariants.BODY1}
                                      textCursor="pointer"
                                      textColor={WsTypoColors.BLACK}
                                      onClick={() => editButtonHandler(e)}
                                    />
                                  </Box>
                                </Box>
                              </WsElementWithPopOver>
                            </Grid>
                          </Grid>
                        )}
                      </Grid>

                      <Box mt={1} p={2} bgcolor={WsColors.White} borderRadius={2}>
                        <Grid container justifyContent="space-between">
                          <Grid item xs={6}>
                            <Grid container alignItems="center">
                              <Box pr={2}>
                                <WsTypography
                                  variant={WsTypoVariants.BODY1}
                                  color={WsTypoColors.BLACK}
                                  bold
                                >
                                  Date & Time:
                                </WsTypography>
                              </Box>
                              <WsTypography
                                variant={WsTypoVariants.BODY1}
                                color={WsTypoColors.BLACK}
                                display="inline"
                              >
                                {`${getDateUsingFormat(
                                  new Date(e.from).toISOString(),
                                  'hh:mm a'
                                )} - ${getDateUsingFormat(
                                  new Date(e.to).toISOString(),
                                  'hh:mm a'
                                )}, ${getDateUsingFormat(
                                  new Date(e.from).toISOString(),
                                  'D MMMM YYYY'
                                )}`}
                              </WsTypography>
                            </Grid>
                          </Grid>
                          <Grid item xs={6}>
                            <Grid container alignItems="center">
                              <Box pr={2}>
                                <WsTypography
                                  variant={WsTypoVariants.BODY1}
                                  color={WsTypoColors.BLACK}
                                  bold
                                >
                                  Attendees:
                                </WsTypography>
                              </Box>
                              <Grid item>
                                {e.attendees && <TeamAvatarGroupEvent attendees={e.attendees} />}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>
                      <Box p={2} bgcolor={WsColors.White} borderRadius={2}>
                        <Grid container justifyContent="space-between">
                          <Grid item xs={6}>
                            <Grid container alignItems="center">
                              <Box pr={2}>
                                <WsTypography
                                  variant={WsTypoVariants.BODY1}
                                  color={WsTypoColors.BLACK}
                                  bold
                                >
                                  Location:
                                </WsTypography>
                              </Box>
                              <WsTypography
                                variant={WsTypoVariants.BODY1}
                                color={WsTypoColors.BLACK}
                              >
                                {e.location}
                              </WsTypography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>

                      {expanded[e.eventId ?? ''] && <Divider variant="middle" sx={{ mt: 3 }} />}
                      <Grid
                        item
                        xs={8}
                        mt={2}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleUpcomingToggle(e.eventId)}
                      >
                        <StyledGrid>
                          <StyledTypography variant={WsTypoVariants.SUB_TITLE}>
                            {expanded[e.eventId ?? '']
                              ? 'Hide Other Details'
                              : 'Show Other Details'}
                          </StyledTypography>
                          {expanded[e.eventId ?? ''] ? (
                            <StyledKeyboardArrowUpIcon />
                          ) : (
                            <StyledKeyboardArrowDownIcon />
                          )}
                        </StyledGrid>
                      </Grid>
                      <Collapse in={expanded[e.eventId ?? '']} timeout="auto" unmountOnExit>
                        <Grid item xs={12}>
                          <Box mt={1} p={2} bgcolor={WsColors.White} borderRadius={2}>
                            <Grid container justifyContent="space-between" mt={0.5}>
                              <Grid item xs={12}>
                                <Grid container alignItems="center">
                                  <Box pr={2}>
                                    <WsTypography
                                      variant={WsTypoVariants.BODY1}
                                      color={WsTypoColors.BLACK}
                                      bold
                                    >
                                      Evaluation Type:
                                    </WsTypography>
                                  </Box>
                                  <WsTypography
                                    variant={WsTypoVariants.BODY1}
                                    color={WsTypoColors.BLACK}
                                  >
                                    Digital Marketing Evaluation
                                  </WsTypography>
                                  <StyledGrid item ml={3}>
                                    <StyledTypography
                                      variant={WsTypoVariants.BODY1}
                                      onClick={() => viewEvaluationHandler(e.evalTemplateId ?? '')}
                                      sx={{ cursor: 'pointer' }}
                                    >
                                      View evaluation
                                    </StyledTypography>
                                    <StyledOpenIcon />
                                  </StyledGrid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} mt={2}>
                                <Grid container alignItems="center">
                                  <Box pr={2}>
                                    <WsTypography
                                      variant={WsTypoVariants.BODY1}
                                      color={WsTypoColors.BLACK}
                                      bold
                                    >
                                      Description:
                                    </WsTypography>
                                  </Box>
                                  <WsTypography
                                    variant={WsTypoVariants.BODY1}
                                    color={WsTypoColors.BLACK}
                                  >
                                    {e.eventDescription}
                                  </WsTypography>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                      </Collapse>
                    </Paper>
                  ))}
                </Grid>
              </Grid>
            ))
            .value()
        ) : (
          <BlankTemplateStyledBox pt={15} pb={15} width={201}>
            <img src={NoEventImage} width={140} height={140} alt="emptyEvent" />
            <Box pt={2}>
              <WsTypography bold>No events to show</WsTypography>
              <WsTypography color={WsTypoColors.TERTIARY900}>
                Newly created items will appear here.
              </WsTypography>
            </Box>
          </BlankTemplateStyledBox>
        )}
      </Grid>

      <WsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} fullHeight>
        <StageStatusCalendarEvent
          onClose={() => setDrawerOpen(false)}
          onSave={() => setDrawerOpen(false)}
          fullWidth
          candidate={candidate}
        />
      </WsDrawer>
      <CalendarEventCardEditDialog
        open={dialogOpen}
        calendarObj={calendarObj as unknown as IOutlookCalendarEvent}
        onEdit={() => setEditDialogOpen(true)}
        onClose={() => setDialogOpen(false)}
        onCopy={() => {}}
        onDelete={() => {}}
      />
      <WsDrawer open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullHeight>
        <StageStatusCalendarEvent
          onClose={() => setEditDialogOpen(false)}
          onSave={() => {
            setDrawerOpen(false);
            setDialogOpen(false);
            setEventsFlag((flag) => !flag);
          }}
          fullWidth
          candidate={candidate}
          defaultValues={{
            ...calendarObj,
            attendees: calendarObj?.attendees?.map((attendee) => ({ email: attendee })) || [],
          }}
          calendarType={calendarObj?.['type']}
        />
      </WsDrawer>
    </>
  );
};
